import ReactSelect from 'react-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

/**
 * Picks the admin who counter-signs an Adobe Sign document_signing step.
 *
 * Three ways to nominate one:
 *   - initiator — whoever sends the document (the default, and what every step
 *                 built before this option existed keeps doing)
 *   - admin     — a specific portal admin
 *   - email     — a typed address, which need not belong to a portal user
 *
 * Used by the step form and the inline flow editor, and again on a task instance
 * where an admin can override the flow's choice for one document.
 *
 * The API keeps these in dedicated columns, hidden from customers — they arrive
 * as countersigner_* on a flow step and countersigner_*_snapshot on an instance
 * step, so callers map them in and out rather than this component guessing.
 */

export const COUNTERSIGNER_TYPES = [
    { value: 'initiator', label: 'Whoever sends the document (default)' },
    { value: 'admin', label: 'A specific admin' },
    { value: 'email', label: 'An email address' },
];

export function countersignerSummary(selection, adminUsers = []) {
    const { type, adminUserId, email, name } = selection || {};

    if (type === 'admin') {
        const admin = (adminUsers || []).find(u => u.id === adminUserId);
        if (admin) return `${admin.firstname} ${admin.lastname}`;
        return name || 'A specific admin';
    }

    if (type === 'email') {
        return name ? `${name} (${email})` : (email || 'An email address');
    }

    return 'Whoever sends the document';
}

/** Read the selection off a task flow step. */
export function countersignerFromFlowStep(step) {
    return {
        type: step?.countersigner_type || 'initiator',
        adminUserId: step?.countersigner_admin_user_id || null,
        email: step?.countersigner_email || '',
        name: step?.countersigner_name || '',
    };
}

/** Read the selection off a task instance step's snapshot. */
export function countersignerFromInstanceStep(step) {
    return {
        type: step?.countersigner_type_snapshot || 'initiator',
        adminUserId: step?.countersigner_admin_user_id_snapshot || null,
        email: step?.countersigner_email_snapshot || '',
        name: step?.countersigner_name_snapshot || '',
    };
}

/**
 * Label for the counter-signer actually resolved onto an agreement, as recorded
 * in step_result.countersigner. Shows the address too — with a shared or
 * external mailbox the name alone doesn't identify who was sent the document.
 */
export function resolvedCountersignerLabel(countersigner) {
    if (!countersigner) return null;

    const { name, email } = countersigner;
    if (name && email) return `${name} (${email})`;

    return name || email || null;
}

/** Flatten a selection into the fields the flow step endpoints expect. */
export function countersignerToRequest(selection) {
    const { type, adminUserId, email, name } = selection || {};

    return {
        countersigner_type: type || 'initiator',
        countersigner_admin_user_id: type === 'admin' ? (adminUserId || null) : null,
        countersigner_email: type === 'email' ? (email || null) : null,
        countersigner_name: type === 'email' ? (name || null) : null,
    };
}

/**
 * Two looks: the roomy form styling used by the step form and the instance
 * dialog, and the compact amber styling the inline flow editor uses for every
 * other control in its document signing block.
 */
const VARIANTS = {
    default: {
        label: 'block text-sm font-medium text-gray-900 dark:text-white mb-2',
        help: 'text-xs text-gray-500 dark:text-gray-400 mb-2',
        input: 'block w-full rounded-md border-0 py-1.5 px-2 text-gray-900 dark:text-white dark:bg-zinc-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-amber-600 sm:text-sm sm:leading-6',
        note: 'mt-1 text-xs text-gray-500 dark:text-gray-400',
        reactSelect: 'text-sm',
    },
    compact: {
        label: 'block text-xs font-medium text-amber-800 dark:text-amber-300 mb-2',
        help: 'text-xs text-gray-500 dark:text-gray-400 mb-2',
        input: 'block w-full rounded-md border-0 py-1 pl-2 pr-8 text-xs text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-zinc-600 dark:bg-zinc-700 focus:ring-2 focus:ring-amber-600',
        note: 'mt-1 text-xs text-gray-500 dark:text-gray-400',
        reactSelect: 'text-xs',
    },
};

export default function CountersignerPicker({
    adminUsers = [],
    type = 'initiator',
    adminUserId = null,
    email = '',
    name = '',
    onChange,
    label = 'Counter-Signer',
    helpText = 'Who signs on behalf of the organisation. Defaults to whoever sends the document.',
    variant = 'default',
    stopPropagation = false,
}) {
    const styles = VARIANTS[variant] ?? VARIANTS.default;

    const adminOptions = (adminUsers || []).map(u => ({
        value: u.id,
        label: `${u.firstname} ${u.lastname}`,
    }));

    const stop = stopPropagation ? (e) => e.stopPropagation() : undefined;

    function update(changes) {
        onChange({ type, adminUserId, email, name, ...changes });
    }

    function changeType(nextType) {
        // Clear the fields belonging to the type being left, so a stale admin id
        // or address can't be saved alongside the new choice.
        update({
            type: nextType,
            adminUserId: nextType === 'admin' ? adminUserId : null,
            email: nextType === 'email' ? email : '',
            name: nextType === 'email' ? name : '',
        });
    }

    return (
        <div onClick={stop}>
            <label className={styles.label}>{label}</label>
            {helpText && <p className={styles.help}>{helpText}</p>}

            {variant === 'compact' ? (
                <select
                    value={type || 'initiator'}
                    onChange={(e) => changeType(e.target.value)}
                    className={styles.input}
                    onClick={stop}
                >
                    {COUNTERSIGNER_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                </select>
            ) : (
                <Select value={type || 'initiator'} onValueChange={changeType}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {COUNTERSIGNER_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            )}

            {type === 'admin' && (
                <div className="mt-2">
                    <ReactSelect
                        value={adminOptions.find(o => o.value === adminUserId) || null}
                        onChange={(option) => update({ adminUserId: option ? option.value : null })}
                        options={adminOptions}
                        placeholder="Search for an admin..."
                        isClearable
                        className={styles.reactSelect}
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                    />
                    <p className={styles.note}>They sign in the portal, and Adobe Sign emails them as well.</p>
                </div>
            )}

            {type === 'email' && (
                <div className="mt-2 space-y-2">
                    <input
                        type="email"
                        value={email || ''}
                        onChange={(e) => update({ email: e.target.value })}
                        placeholder="e.g. lettings@example.org"
                        className={styles.input}
                        onClick={stop}
                    />
                    <input
                        type="text"
                        value={name || ''}
                        onChange={(e) => update({ name: e.target.value })}
                        placeholder="Name (optional)"
                        className={styles.input}
                        onClick={stop}
                    />
                    <p className={styles.note}>
                        If the address doesn't belong to a portal admin, they sign via the link Adobe Sign emails them.
                    </p>
                </div>
            )}
        </div>
    );
}
